function isPositivePrice(value: unknown): boolean {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

/**
 * Once a sell price is specified, the buy price must be a positive number.
 * While the sell price is unspecified (zero/empty) the buy price is not
 * validated. `v-model.number` yields an empty string when an input is
 * cleared or unparseable, so anything non-numeric counts as unspecified.
 */
export function isValidBuyPrice(
  buyPrice: unknown,
  sellPrice: unknown
): boolean {
  if (!isPositivePrice(sellPrice)) return true;
  return isPositivePrice(buyPrice);
}

/**
 * Derive the buy price to show when opening an existing transaction.
 *
 * A swap stamps one buy price onto every payable item, so only a uniform
 * non-null override across ALL payable items (differing from the sell price)
 * hydrates as a swap. Zero-amount auxiliary items (e.g. the Soldera fee item
 * added on completion, which carries no price) are ignored. Anything else —
 * mixed overrides, partial overrides, no overrides — is a regular transaction
 * whose buy price mirrors the sell price; hydrating the first override as the
 * buy price used to misclassify the next save as a swap and flatten every
 * per-item price to it.
 */
export function hydrateBuyPrice(
  items: { amount: number; price: number | null }[],
  unitPrice: number
): { buyPrice: number; isSwap: boolean } {
  const prices = items.filter((i) => i.amount > 0).map((i) => i.price);
  const uniformPrice =
    prices.length > 0 && prices.every((p) => p !== null && p === prices[0])
      ? prices[0]
      : null;
  if (uniformPrice !== null && uniformPrice !== unitPrice) {
    return { buyPrice: uniformPrice, isSwap: true };
  }
  return { buyPrice: unitPrice, isSwap: false };
}

/**
 * Whether saving should stamp the single buy price onto every item
 * (destroying per-item overrides). The last price input the user touched
 * wins: a buy-price edit flattens, a per-item edit is preserved. With
 * neither touched, a transaction that loaded as a swap keeps its uniform
 * buy price (a no-op rewrite). Price divergence alone never flattens — it
 * also arises from a stale buy price after a sell price edit.
 */
export function shouldApplyBuyPriceToAllItems(flags: {
  isSwap: boolean;
  lastPriceEdit: "buy" | "items" | null;
  hydratedAsSwap: boolean;
}): boolean {
  if (!flags.isSwap) return false;
  if (flags.lastPriceEdit !== null) return flags.lastPriceEdit === "buy";
  return flags.hydratedAsSwap;
}

/**
 * Whether any item carries a price override. An override makes the swap
 * referrer eligible for a payout on completion, so the referrer field must
 * stay editable whenever this holds — even when the top-level buy price
 * mirrors the sell price. The payout itself is still gated by the backend:
 * it also requires a referrer, a transaction date, a sell price, a positive
 * sell-minus-buy margin, and a referral rate.
 */
export function hasItemPriceOverride(
  itemPrices: Record<number, number | null>
): boolean {
  return Object.values(itemPrices).some((p) => p !== null);
}

/**
 * The per-item price a buy price translates to: the buy price itself while
 * it diverges from the sell price, or null (clear the overrides — no swap
 * margin) once they match.
 */
export function buyPriceToItemPrice(
  buyPrice: number,
  unitPrice: number
): number | null {
  return buyPrice === unitPrice ? null : buyPrice;
}

/**
 * Build a per-item price-override map that applies one buy price to every transaction item.
 * `null` buy price clears the overrides (buy == sell => no swap margin).
 */
export function buildItemPricesFromBuyPrice(
  itemKeys: number[],
  buyPrice: number | null
): Record<number, number | null> {
  const result: Record<number, number | null> = {};
  for (const key of itemKeys) {
    result[key] = buyPrice;
  }
  return result;
}
